import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSystem } from '@ohif/core';
import EscalationContactsSection, {
  type EscalationContactsSectionHandle,
} from './EscalationContactsSection';
import EscalationMatrixSection, {
  type EscalationMatrixSectionHandle,
} from './EscalationMatrixSection';
import EscalationContactFormDialog from './EscalationContactFormDialog';
import {
  createEscalationContact,
  patchEscalationContactStatus,
  updateEscalationContact,
} from '../../services/escalationContactService';
import type {
  EscalationContact,
  EscalationContactFormValues,
  EscalationContactRole,
  EscalationContactStatus,
} from '../../types/escalationContact.types';

interface CriticalEscalationTabProps {
  facilityId: number;
  drawerContainerRef: React.RefObject<HTMLDivElement>;
  onDrawerOpenChange: (open: boolean) => void;
}

export default function CriticalEscalationTab({
  facilityId,
  drawerContainerRef,
  onDrawerOpenChange,
}: Readonly<CriticalEscalationTabProps>) {
  const { servicesManager } = useSystem();
  const { uiNotificationService } = servicesManager.services;
  const contactsRef = useRef<EscalationContactsSectionHandle>(null);
  const matrixRef = useRef<EscalationMatrixSectionHandle>(null);

  const syncMatrixContacts = useCallback(() => {
    void matrixRef.current?.refreshActiveContacts();
  }, []);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'add' | 'edit'>('add');
  const [selectedContact, setSelectedContact] = useState<EscalationContact | null>(null);
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});

  const setDrawer = (open: boolean) => {
    setDrawerOpen(open);
    onDrawerOpenChange(open);
  };

  const openAddDrawer = () => {
    setDialogMode('add');
    setSelectedContact(null);
    setServerErrors({});
    setDrawer(true);
  };

  const openEditDrawer = (contact: EscalationContact) => {
    setDialogMode('edit');
    setSelectedContact(contact);
    setServerErrors({});
    setDrawer(true);
  };

  const closeDrawer = () => {
    if (saving) {
      return;
    }
    setDrawer(false);
  };

  const handleSave = async (values: EscalationContactFormValues) => {
    setSaving(true);
    setServerErrors({});
    const payload = {
      facilityId,
      name: values.name,
      role: values.role as EscalationContactRole,
      department: values.department || null,
      mobileNumber: values.mobileNumber,
      alternateMobileNumber: values.alternateMobileNumber || null,
    };

    const res =
      dialogMode === 'add'
        ? await createEscalationContact(payload)
        : await updateEscalationContact(selectedContact!.id, payload);

    if (res.success && res.data) {
      const targetStatus: EscalationContactStatus = values.active ? 'Active' : 'Inactive';
      if (res.data.status !== targetStatus) {
        const statusRes = await patchEscalationContactStatus(res.data.id, facilityId, targetStatus);
        if (!statusRes.success) {
          setSaving(false);
          uiNotificationService?.show({
            title: 'Contact saved, status not updated',
            message: statusRes.error || 'Unable to update contact status',
            type: 'error',
            duration: 4000,
          });
          setDrawer(false);
          contactsRef.current?.reload();
          syncMatrixContacts();
          return;
        }
      }
    }

    setSaving(false);

    if (res.success) {
      uiNotificationService?.show({
        title: dialogMode === 'add' ? 'Contact added' : 'Contact updated',
        message: `${values.name} has been saved.`,
        type: 'success',
        duration: 3000,
      });
      setDrawer(false);
      contactsRef.current?.reload();
      syncMatrixContacts();
      return;
    }

    if (res.fieldErrors) {
      setServerErrors(res.fieldErrors);
    }

    uiNotificationService?.show({
      title: 'Save failed',
      message: res.error || 'Unable to save contact',
      type: 'error',
      duration: 4000,
    });
  };

  const drawerNode = (
    <EscalationContactFormDialog
      inline
      open={drawerOpen}
      mode={dialogMode}
      initialContact={selectedContact}
      saving={saving}
      serverErrors={serverErrors}
      onClose={closeDrawer}
      onSubmit={handleSave}
    />
  );

  return (
    <div className="ris-config-tab-panel">
      <EscalationContactsSection
        ref={contactsRef}
        facilityId={facilityId}
        onAddContact={openAddDrawer}
        onEditContact={openEditDrawer}
        onContactsUpdated={syncMatrixContacts}
      />
      <EscalationMatrixSection
        ref={matrixRef}
        facilityId={facilityId}
      />
      {drawerContainerRef.current && drawerOpen
        ? createPortal(drawerNode, drawerContainerRef.current)
        : null}
    </div>
  );
}
